import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Tooltip } from 'primereact/tooltip';
import { useGetHostGroupsQuery } from '../../api/groupApi';
import {
  useGetGroupGiveawaysQuery,
  useSendAlertMutation,
  useScheduleAlertMutation,
  useEndGiveawayMutation,
} from '../../api/giveawayApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import useAutoSelectFirst from '../../hooks/useAutoSelectFirst';
import StatusDots from '../../components/StatusDots/StatusDots';
import MessageField from '../../components/MessageField/MessageField';
import { messageMaxLengthForChannels } from '../../utils/messageLimits';
import useGroupActivityDots from '../../hooks/useGroupActivityDots';
import s from '../../styles/shared/page.module.css';
import buttons from '../../styles/shared/buttons.module.css';
import table from '../../styles/shared/table.module.css';
import styles from './GiveawaysPage.module.css';

function defaultScheduleDate() {
  return new Date(Date.now() + 60 * 60 * 1000);
}

function formatScheduleDate(date) {
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

export default function GiveawaysPage() {
  const { data: groups, isLoading: loadingGroups } = useGetHostGroupsQuery();
  const [groupId, setGroupId] = useState('');
  const navigate = useNavigate();
  const showToast = useToast();
  const { activeGroupIds, clear: clearActivityDot } = useGroupActivityDots();

  useAutoSelectFirst(groups, groupId, setGroupId);
  const activeGroupChannels = groups?.find((g) => g.id === groupId)?.channels;

  const {
    data: giveaways,
    isLoading: loadingGiveaways,
    isError: giveawaysError,
    error: giveawaysLoadError,
  } = useGetGroupGiveawaysQuery(groupId, { skip: !groupId });

  const [sendAlert, { isLoading: isSendingAlert }] = useSendAlertMutation();
  const [scheduleAlert, { isLoading: isScheduling }] = useScheduleAlertMutation();
  const [endGiveaway] = useEndGiveawayMutation();

  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleMessage, setScheduleMessage] = useState('');

  const [alertTarget, setAlertTarget] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');

  useEffect(() => {
    if (giveawaysError) showToast('error', 'Giveaways', errMsg(giveawaysLoadError, 'Could not load giveaways.'));
  }, [giveawaysError, giveawaysLoadError, showToast]);

  const tooltipRef = useRef(null);
  useEffect(() => {
    tooltipRef.current?.updateTargetEvents();
  }, [giveaways]);

  function openAlertDialog(giveaway) {
    setAlertTarget(giveaway);
    setAlertMessage('');
  }

  function closeAlertDialog() {
    setAlertTarget(null);
    setAlertMessage('');
  }

  async function handleSendAlert() {
    try {
      const res = await sendAlert({ id: alertTarget.id, groupId, message: alertMessage.trim() }).unwrap();
      const a = res.audience;
      showToast(
        'success',
        'Giveaways',
        `Alert #${res.alert_count} sent — email: ${a.email}, text: ${a.text}, telegram: ${a.telegram}.`,
      );
      closeAlertDialog();
    } catch (err) {
      showToast('error', 'Giveaways', errMsg(err, 'Could not send alert.'));
    }
  }

  function openScheduleDialog(giveaway) {
    setScheduleTarget(giveaway);
    setScheduleDate(defaultScheduleDate());
    setScheduleMessage('');
  }

  function closeScheduleDialog() {
    setScheduleTarget(null);
    setScheduleDate(null);
    setScheduleMessage('');
  }

  async function handleSaveSchedule() {
    if (!scheduleDate) return;
    try {
      await scheduleAlert({
        id: scheduleTarget.id,
        scheduledAt: scheduleDate.toISOString(),
        message: scheduleMessage.trim(),
      }).unwrap();
      showToast('success', 'Giveaways', `Alert scheduled for ${formatScheduleDate(scheduleDate)}.`);
      closeScheduleDialog();
    } catch (err) {
      showToast('error', 'Giveaways', errMsg(err, 'Could not schedule alert.'));
    }
  }

  async function handleEnd(giveaway) {
    try {
      await endGiveaway({ id: giveaway.id, groupId }).unwrap();
      showToast('success', 'Giveaways', 'Giveaway ended.');
    } catch (err) {
      showToast('error', 'Giveaways', errMsg(err, 'Could not end giveaway.'));
    }
  }

  const goServings = (giveaway) => navigate(`${giveaway.id}/servings`);
  const goMessages = () => {
    clearActivityDot(groupId);
    navigate(`/host/groups/${groupId}/messages`);
  };

  const actionBodyTemplate = (giveaway) => {
    const isEnded = giveaway.status === 'ended';
    return (
      <div className={styles.actionsGrid}>
        <Button
          icon="pi pi-pencil"
          text
          disabled={isEnded}
          className={table.iconNeutral}
          onClick={() => goServings(giveaway)}
        />
        <Button
          icon="pi pi-megaphone"
          text
          disabled={isEnded}
          className={table.iconNeutral}
          onClick={() => openAlertDialog(giveaway)}
        />
        <Button
          icon="pi pi-calendar-plus"
          text
          disabled={isEnded}
          className={table.iconNeutral}
          onClick={() => openScheduleDialog(giveaway)}
          aria-label="Schedule an alert"
        />
        <span className={table.iconWithDot}>
          <Button icon="pi pi-comments" text className={table.iconNeutral} onClick={goMessages} />
          {activeGroupIds.has(groupId) && <span className={table.newMessageDot} />}
        </span>
        <Button
          icon="pi pi-ban"
          text
          disabled={isEnded}
          className={table.iconDisable}
          onClick={() => handleEnd(giveaway)}
        />
      </div>
    );
  };

  if (loadingGroups) return <p className="textMuted">Loading…</p>;
  if (!groups?.length)
    return (
      <div className={s.page}>
        <h1>Giveaways</h1>
        <p className={`${s.subtitle} textMuted`}>
          No enabled groups yet — ask a manager to enable one first.
        </p>
      </div>
    );

  return (
    <div className={s.page}>
      <div className={s.header}>
        <div>
          <h1>Giveaways</h1>
          <p className={`${s.subtitle} textMuted`}>Food giveaways for your group.</p>
        </div>
        <Button
          label="Add giveaway"
          icon="pi pi-plus"
          className={buttons.btnPrimary}
          onClick={() => navigate('create', { state: { groupId } })}
        />
      </div>

      <div className={`field ${s.orgSelect}`}>
        <label htmlFor="group">Group</label>
        <Dropdown
          inputId="group"
          value={groupId}
          onChange={(e) => setGroupId(e.value)}
          options={groups.map((g) => ({ label: g.name, value: g.id }))}
          className="w-full"
        />
      </div>

      <div className={table.tableWrapper}>
        <Tooltip ref={tooltipRef} target={`.${table.statusDot}, .${styles.tooltipTarget}`} position="left" />
        <DataTable
          value={giveaways ?? []}
          loading={loadingGiveaways}
          emptyMessage="No giveaways yet. Add one above."
          responsiveLayout="stack"
        >
          <Column
            field="title"
            header="Title"
            className={table.contactCol}
            body={(g) => (
              <div className={`${table.cellInsider} ${table.cellWrap}`}>
                <span className={`${styles.clamp2} ${styles.tooltipTarget}`} data-pr-tooltip={g.title}>
                  {g.title}
                </span>
              </div>
            )}
          />
          <Column
            header="Location"
            className={`${s.mutedCell} ${table.contactCol}`}
            body={(g) => (
              <div className={`${table.cellInsider} ${table.cellWrap}`}>
                <span className={`${styles.clamp2} ${styles.tooltipTarget}`} data-pr-tooltip={g.location}>
                  {g.location}
                </span>
              </div>
            )}
          />
          <Column
            header="Servings left"
            className={table.contactCol}
            style={{ width: '120px' }}
            body={(g) => <div className={table.cellInsider}>{g.servings_left ?? '—'}</div>}
          />
          <Column
            header="Status"
            className={table.statusCol}
            style={{ width: '130px' }}
            body={(g) => {
              const isEnded = g.status === 'ended';
              return (
                <div className={table.cellInsider}>
                  <div className={table.statusBadgesDesktop}>
                    <span className={isEnded ? s.statusDisabled : s.statusOk}>
                      {isEnded ? 'ended' : 'active'}
                    </span>
                  </div>
                  <StatusDots
                    items={[
                      {
                        label: isEnded ? 'ended' : 'active',
                        color: isEnded ? 'var(--color-error)' : 'var(--color-success)',
                      },
                    ]}
                  />
                </div>
              );
            }}
          />
          <Column
            header="Actions"
            className={table.actionsCol}
            body={actionBodyTemplate}
            style={{ width: '200px' }}
          />
        </DataTable>
      </div>

      <Dialog
        header={`Schedule: ${scheduleDate ? formatScheduleDate(scheduleDate) : '—'}`}
        visible={Boolean(scheduleTarget)}
        onHide={closeScheduleDialog}
        style={{ width: '520px' }}
        className={styles.scheduleDialog}
        modal
      >
        <Calendar
          inline
          showTime
          hourFormat="24"
          value={scheduleDate}
          onChange={(e) => setScheduleDate(e.value)}
          minDate={new Date()}
          className="w-full"
          panelClassName={styles.scheduleCalendarPanel}
        />
        <MessageField
          id="scheduleMessage"
          label="Message (optional)"
          value={scheduleMessage}
          onChange={setScheduleMessage}
          rows={3}
          placeholder="Leave blank to send the default alert (giveaway details)."
          maxLength={messageMaxLengthForChannels(activeGroupChannels)}
        />
        <div className={styles.scheduleDialogActions}>
          <Button
            type="button"
            label="Schedule"
            className={`${buttons.saveBtn} ${styles.scheduleSaveBtn}`}
            onClick={handleSaveSchedule}
            loading={isScheduling}
            disabled={!scheduleDate}
          />
        </div>
      </Dialog>

      <Dialog
        header={`Send alert${alertTarget ? ` — ${alertTarget.title}` : ''}`}
        visible={Boolean(alertTarget)}
        onHide={closeAlertDialog}
        style={{ width: '520px' }}
        modal
      >
        <MessageField
          id="alertMessage"
          label="Message (optional)"
          value={alertMessage}
          onChange={setAlertMessage}
          placeholder="Leave blank to send the default alert (giveaway details)."
          maxLength={messageMaxLengthForChannels(activeGroupChannels)}
        />
        <Button
          label="Send"
          className={buttons.saveBtn}
          loading={isSendingAlert}
          onClick={handleSendAlert}
        />
      </Dialog>
    </div>
  );
}
